import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataTableValue } from 'primereact/datatable';
import { getExpandedIndex } from '../utils/getExpandedIndex';

type UseExpandedRowTrackingProps = {
  data: DataTableValue[] | null | undefined;
  expandedRow: DataTableValue | null | undefined;
  dataKey: string;
}

export const useExpandedRowTracking = ({
  data,
  expandedRow,
  dataKey,
}: UseExpandedRowTrackingProps) => {
  const [expandedRowHeight, setExpandedRowHeight] = useState<number>(0);
  // Non-reactive mirror of the held height so wrappedRowExpansionTemplate can read
  // it without becoming a useCallback dep (which would re-render the expansion on
  // every measurement).
  const heldExpandedHeightRef = useRef<number>(0);

  const expandedIndex: number = useMemo(
    () => getExpandedIndex(data, expandedRow, dataKey),
    [data, expandedRow, dataKey]
  );

  const handleExpandedHeightChange = useCallback((height: number) => {
    // Track the latest committed height. ExpandedRowAnimation only reports settled
    // heights (it holds the transient short height itself during a remount), so
    // tracking latest keeps the virtualScroller compensated on scroll AND lets the
    // row grow/shrink on tab switches. The 0 guard drops the unmount measurement.
    if (height <= 0) return;
    heldExpandedHeightRef.current = height;
    setExpandedRowHeight(height);
  }, []);

  // Reset the held height when the expanded row changes (open, switch, collapse) so
  // a new expansion re-measures from scratch; a same-row remount keeps it.
  const expandedRowKey = expandedRow ? String(expandedRow[dataKey]) : null;
  useEffect(() => {
    heldExpandedHeightRef.current = 0;
    setExpandedRowHeight(0);
  }, [expandedRowKey]);

  return {
    expandedIndex,
    expandedRowHeight,
    setExpandedRowHeight,
    heldExpandedHeightRef,
    handleExpandedHeightChange,
  };
};
